import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate, spring, useVideoConfig, Img, Easing } from "remotion";

// 效果 61: 图片过渡 - 淡入淡出
export const ImageFadeIn: React.FC<{
  imageUrl: string;
  duration?: number;
}> = ({ imageUrl, duration = 60 }) => {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, duration], [0, 1], {
    extrapolateRight: "clamp",
  });
  
  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      <Img
        src={imageUrl}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity,
        }}
      />
    </AbsoluteFill>
  );
};

// 效果 62: 图片过渡 - 缩放进入
export const ImageZoomIn: React.FC<{
  imageUrl: string;
  duration?: number;
}> = ({ imageUrl, duration = 60 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const scale = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 100 },
  });
  
  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      <Img
        src={imageUrl}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: `scale(${scale})`,
        }}
      />
    </AbsoluteFill>
  );
};

// 效果 63: 图片过渡 - 滑动进入
export const ImageSlideIn: React.FC<{
  imageUrl: string;
  direction?: "left" | "right" | "top" | "bottom";
  duration?: number;
}> = ({ imageUrl, direction = "left", duration = 60 }) => {
  const frame = useCurrentFrame();
  const progress = interpolate(frame, [0, duration], [0, 1], {
    extrapolateRight: "clamp",
  });
  
  const getTransform = () => {
    const offset = (1 - progress) * 100;
    switch (direction) {
      case "left":
        return `translateX(-${offset}%)`;
      case "right":
        return `translateX(${offset}%)`;
      case "top":
        return `translateY(-${offset}%)`;
      case "bottom":
        return `translateY(${offset}%)`;
      default:
        return "none";
    }
  };
  
  return (
    <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
      <Img
        src={imageUrl}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: getTransform(),
        }}
      />
    </AbsoluteFill>
  );
};

// 效果 64: 图片过渡 - Ken Burns 效果（慢速缩放）
export const ImageKenBurns: React.FC<{
  imageUrl: string;
  zoomDirection?: "in" | "out";
}> = ({ imageUrl, zoomDirection = "in" }) => {
  const frame = useCurrentFrame();
  const scale = zoomDirection === "in" 
    ? interpolate(frame, [0, 120], [1, 1.3])
    : interpolate(frame, [0, 120], [1.3, 1]);
  
  return (
    <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
      <Img
        src={imageUrl}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: `scale(${scale})`,
        }}
      />
    </AbsoluteFill>
  );
};

// 效果 65: 图片过渡 - 模糊到清晰
export const ImageBlurIn: React.FC<{
  imageUrl: string;
  duration?: number;
}> = ({ imageUrl, duration = 60 }) => {
  const frame = useCurrentFrame();
  const blur = interpolate(frame, [0, duration], [20, 0], {
    extrapolateRight: "clamp",
  });
  
  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      <Img
        src={imageUrl}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          filter: `blur(${blur}px)`,
        }}
      />
    </AbsoluteFill>
  );
};

// 效果 66: 图片过渡 - 遮罩揭示
export const ImageMaskReveal: React.FC<{
  imageUrl: string;
  shape?: "circle" | "square";
}> = ({ imageUrl, shape = "circle" }) => {
  const frame = useCurrentFrame();
  const progress = interpolate(frame, [0, 60], [0, 150], {
    extrapolateRight: "clamp",
  });
  
  const clipPath = shape === "circle"
    ? `circle(${progress}% at 50% 50%)`
    : `inset(${100 - progress}%)`;
  
  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      <Img
        src={imageUrl}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          clipPath,
        }}
      />
    </AbsoluteFill>
  );
};

// 效果 67: 图片过渡 - 分割滑动
export const ImageSplitSlide: React.FC<{
  imageUrl: string;
}> = ({ imageUrl }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const progress = spring({
    frame,
    fps,
    config: { damping: 20, stiffness: 100 },
  });
  
  const leftX = interpolate(progress, [0, 1], [-50, 0]);
  const rightX = interpolate(progress, [0, 1], [50, 0]);
  
  return (
    <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
      <div style={{ position: "absolute", left: leftX, top: 0, width: "50%", height: "100%" }}>
        <Img
          src={imageUrl}
          style={{
            width: "200%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            left: 0,
          }}
        />
      </div>
      <div style={{ position: "absolute", right: rightX, top: 0, width: "50%", height: "100%" }}>
        <Img
          src={imageUrl}
          style={{
            width: "200%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            right: 0,
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

// 效果 68: 图片过渡 - 网格揭示
export const ImageGridReveal: React.FC<{
  imageUrl: string;
  gridSize?: number;
}> = ({ imageUrl, gridSize = 4 }) => {
  const frame = useCurrentFrame();
  
  return (
    <AbsoluteFill style={{ backgroundColor: "#000", display: "grid", gridTemplateColumns: `repeat(${gridSize}, 1fr)` }}>
      {Array.from({ length: gridSize * gridSize }, (_, i) => {
        const delay = (i % gridSize + Math.floor(i / gridSize)) * 5;
        const currentFrame = Math.max(0, frame - delay);
        const opacity = interpolate(currentFrame, [0, 20], [0, 1], {
          extrapolateRight: "clamp",
        });
        const scale = interpolate(currentFrame, [0, 20], [0.5, 1], {
          extrapolateRight: "clamp",
        });
        
        return (
          <div
            key={i}
            style={{
              position: "relative",
              overflow: "hidden",
              opacity,
              transform: `scale(${scale})`,
            }}
          >
            <Img
              src={imageUrl}
              style={{
                position: "absolute",
                width: `${gridSize * 100}%`,
                height: `${gridSize * 100}%`,
                left: `-${(i % gridSize) * 100}%`,
                top: `-${Math.floor(i / gridSize) * 100}%`,
                objectFit: "cover",
              }}
            />
          </div>
        );
      })}
    </AbsoluteFill>
  );
};

// 效果 69: 图片过渡 - 溶解效果（电影级噪点溶解 + Ken Burns + 内容层）
export const ImageDissolve: React.FC<{
  imageUrl: string;
  title?: string;
  subtitle?: string;
  caption?: string;
  number?: string;
  cols?: number;
  rows?: number;
}> = ({
  imageUrl,
  title = "Dissolve",
  subtitle = "噪点溶解 · 电影级转场",
  caption = "FRAMEFUTURE · CINEMATIC",
  number = "09",
  cols = 48,
  rows = 27,
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  // —— 动画阶段控制 ——
  // 0 ~ dissolveEnd：粒子溶解显现图片
  // contentStart 起：文字与装饰元素错落登场
  const dissolveEnd = Math.min(70, Math.floor(durationInFrames * 0.55));
  const contentStart = Math.max(0, dissolveEnd - 18);

  // —— Ken Burns 缓慢推进（整段贯穿）——
  const kbScale = interpolate(
    frame,
    [0, durationInFrames],
    [1.08, 1.18],
    { extrapolateRight: "clamp" }
  );
  const kbShiftX = interpolate(
    frame,
    [0, durationInFrames],
    [-1.2, 1.2],
    { extrapolateRight: "clamp" }
  );

  // —— 图片自身的溶解（底层整体淡入，补足粒子之间的缝隙）——
  const imageReveal = interpolate(frame, [0, dissolveEnd], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // —— 粒子网格：每个格子随机延迟淡出，露出图片 ——
  // 使用 cos/sin 伪随机，保证可预测、无抖动
  const cells: React.ReactNode[] = [];
  const cellW = 100 / cols;
  const cellH = 100 / rows;
  const total = cols * rows;

  for (let i = 0; i < total; i++) {
    const x = i % cols;
    const y = Math.floor(i / cols);

    // 以中心为基准的归一化距离（0~1），用作溶解顺序的主导因子
    const nx = (x + 0.5) / cols - 0.5;
    const ny = (y + 0.5) / rows - 0.5;
    const dist = Math.sqrt(nx * nx + ny * ny) / 0.71; // 0~1

    // 伪随机（固定种子）
    const r =
      Math.abs(
        Math.sin(x * 12.9898 + y * 78.233 + (x + y) * 0.137) * 43758.5453
      ) % 1;

    // 延迟：中心先溶解 + 随机扰动
    const delay = dist * dissolveEnd * 0.55 + r * dissolveEnd * 0.5;
    const fadeOut = interpolate(
      frame,
      [delay, delay + 10],
      [1, 0],
      { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
    );

    if (fadeOut <= 0.02) continue; // 跳过已完全消散的，降低渲染负担

    // 粒子初始颜色：近黑带轻微明暗变化，模拟噪点
    const shade = 8 + Math.floor(r * 28);
    const micro = (r - 0.5) * 2; // -1~1 轻微位移
    cells.push(
      <div
        key={i}
        style={{
          position: "absolute",
          left: `${x * cellW}%`,
          top: `${y * cellH}%`,
          width: `${cellW + 0.4}%`,
          height: `${cellH + 0.4}%`,
          backgroundColor: `rgb(${shade}, ${shade}, ${shade + 2})`,
          opacity: fadeOut,
          transform: `translate(${micro * 2}px, ${micro * 2}px)`,
          willChange: "opacity, transform",
        }}
      />
    );
  }

  // —— 内容层动画（spring 弹入）——
  const contentFrame = Math.max(0, frame - contentStart);
  const titleProgress = spring({
    frame: contentFrame,
    fps,
    config: { damping: 18, stiffness: 120, mass: 0.9 },
  });
  const subtitleProgress = spring({
    frame: contentFrame - 8,
    fps,
    config: { damping: 20, stiffness: 110 },
  });
  const captionProgress = spring({
    frame: contentFrame - 16,
    fps,
    config: { damping: 22, stiffness: 100 },
  });
  const lineProgress = interpolate(
    contentFrame,
    [0, 28],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );
  const numberProgress = spring({
    frame: contentFrame - 4,
    fps,
    config: { damping: 16, stiffness: 140 },
  });

  // —— 扫描光带（溶解阶段快速掠过）——
  const scanY = interpolate(
    frame,
    [0, dissolveEnd],
    [-20, 120],
    { extrapolateRight: "clamp" }
  );
  const scanOpacity = interpolate(
    frame,
    [0, dissolveEnd * 0.4, dissolveEnd],
    [0, 0.45, 0],
    { extrapolateRight: "clamp" }
  );

  return (
    <AbsoluteFill style={{ backgroundColor: "#050507", overflow: "hidden" }}>
      {/* 底层图片 + Ken Burns */}
      <AbsoluteFill
        style={{
          transform: `scale(${kbScale}) translate(${kbShiftX}%, 0)`,
          opacity: imageReveal,
        }}
      >
        <Img
          src={imageUrl}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </AbsoluteFill>

      {/* 粒子溶解层 */}
      <AbsoluteFill>{cells}</AbsoluteFill>

      {/* 扫描光带 */}
      <AbsoluteFill
        style={{
          background: `linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)`,
          height: "14%",
          top: `${scanY}%`,
          opacity: scanOpacity,
          mixBlendMode: "screen",
          pointerEvents: "none",
        }}
      />

      {/* 胶片颗粒（静态噪点，极低透明度） */}
      <AbsoluteFill
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
          mixBlendMode: "overlay",
          opacity: 0.35,
          pointerEvents: "none",
        }}
      />

      {/* 底部渐变蒙层，提升文字可读性 */}
      <AbsoluteFill
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.55) 78%, rgba(0,0,0,0.85) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* 暗角 */}
      <AbsoluteFill
        style={{
          boxShadow: "inset 0 0 220px 40px rgba(0,0,0,0.75)",
          pointerEvents: "none",
        }}
      />

      {/* 顶部 caption + 编号 */}
      <div
        style={{
          position: "absolute",
          top: 40,
          left: 56,
          right: 56,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          opacity: captionProgress,
          transform: `translateY(${(1 - captionProgress) * -10}px)`,
          color: "rgba(255,255,255,0.82)",
          letterSpacing: "0.32em",
          fontSize: 14,
          fontWeight: 500,
          fontFamily:
            "'SF Mono', 'JetBrains Mono', 'Courier New', monospace",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#ff3b30",
              boxShadow: "0 0 10px #ff3b30",
            }}
          />
          REC · {caption}
        </div>
        <div>{`EFFECT // 069`}</div>
      </div>

      {/* 右上角大号编号 */}
      <div
        style={{
          position: "absolute",
          top: 70,
          right: 56,
          color: "rgba(255,255,255,0.14)",
          fontSize: 180,
          fontWeight: 900,
          lineHeight: 1,
          letterSpacing: "-0.04em",
          fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
          transform: `translateX(${(1 - numberProgress) * 40}px) scale(${
            0.9 + numberProgress * 0.1
          })`,
          opacity: numberProgress * 0.9,
          mixBlendMode: "overlay",
          pointerEvents: "none",
        }}
      >
        {number}
      </div>

      {/* 主内容：左下角的标题块 */}
      <div
        style={{
          position: "absolute",
          left: 56,
          bottom: 72,
          right: 56,
          color: "#fff",
          fontFamily: "'Inter', 'PingFang SC', 'Helvetica Neue', sans-serif",
        }}
      >
        {/* 装饰竖线 */}
        <div
          style={{
            width: 3,
            height: 64 * lineProgress,
            background:
              "linear-gradient(180deg, #ffffff 0%, rgba(255,255,255,0.2) 100%)",
            marginBottom: 18,
          }}
        />

        {/* 主标题 */}
        <div
          style={{
            overflow: "hidden",
            marginBottom: 14,
          }}
        >
          <div
            style={{
              fontSize: 88,
              fontWeight: 800,
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
              transform: `translateY(${(1 - titleProgress) * 100}%)`,
              textShadow: "0 4px 24px rgba(0,0,0,0.5)",
            }}
          >
            {title}
          </div>
        </div>

        {/* 副标题 */}
        <div
          style={{
            overflow: "hidden",
          }}
        >
          <div
            style={{
              fontSize: 24,
              fontWeight: 400,
              letterSpacing: "0.08em",
              color: "rgba(255,255,255,0.82)",
              transform: `translateY(${(1 - subtitleProgress) * 100}%)`,
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <span
              style={{
                width: 36,
                height: 1,
                background: "rgba(255,255,255,0.6)",
                display: "inline-block",
              }}
            />
            {subtitle}
          </div>
        </div>
      </div>

      {/* 右下角时间码 */}
      <div
        style={{
          position: "absolute",
          right: 56,
          bottom: 48,
          color: "rgba(255,255,255,0.7)",
          fontFamily:
            "'SF Mono', 'JetBrains Mono', 'Courier New', monospace",
          fontSize: 14,
          letterSpacing: "0.18em",
          opacity: captionProgress,
        }}
      >
        {`TC ${String(Math.floor(frame / fps))
          .padStart(2, "0")}:${String(frame % fps)
          .padStart(2, "0")} / ${String(
          Math.floor(durationInFrames / fps)
        ).padStart(2, "0")}:${String(durationInFrames % fps).padStart(
          2,
          "0"
        )}`}
      </div>
    </AbsoluteFill>
  );
};

// 效果 70: 图片过渡 - 色块分离（RGB Glitch）
export const ImageColorSplit: React.FC<{
  imageUrl: string;
  title?: string;
  subtitle?: string;
  caption?: string;
  number?: string;
}> = ({
  imageUrl,
  title = "CHROMATIC",
  subtitle = "RGB 色彩分离 · 故障艺术",
  caption = "FRAMEFUTURE · GLITCH LAB",
  number = "70",
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  // —— 确定性伪随机 ——
  const hash = (seed: number) => {
    const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
    return x - Math.floor(x);
  };

  // —— 时间轴 ——
  const introEnd = Math.min(24, Math.floor(durationInFrames * 0.18));
  const outroStart = Math.max(
    introEnd + 30,
    durationInFrames - Math.min(24, Math.floor(durationInFrames * 0.18))
  );

  // —— 基础色彩分离位移：进入时剧烈 → 稳定期微弱浮动 → 退出时再次爆发 ——
  const introSplit = interpolate(frame, [0, introEnd], [120, 10], {
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const outroSplit = interpolate(
    frame,
    [outroStart, durationInFrames],
    [10, 140],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: Easing.in(Easing.cubic),
    }
  );
  const breath = Math.sin(frame * 0.12) * 2.2; // 稳定期呼吸式浮动
  const baseSplit =
    frame < introEnd ? introSplit : Math.max(outroSplit, 10 + breath);

  // —— 周期性 glitch 爆发：每 ~10 帧一个判定窗口，概率触发强烈位移 ——
  const glitchWindow = Math.floor(frame / 10);
  const glitchActive = hash(glitchWindow) > 0.72;
  const glitchStrength = glitchActive
    ? (hash(glitchWindow + 1) - 0.5) * 80
    : 0;
  const glitchY = glitchActive ? (hash(glitchWindow + 2) - 0.5) * 16 : 0;

  const rX = baseSplit + glitchStrength;
  const bX = -baseSplit - glitchStrength * 0.7;
  const gY = glitchY;

  // —— 文字 glitch ——
  const titleProgress = spring({
    frame: frame - introEnd + 8,
    fps,
    config: { damping: 18, stiffness: 130, mass: 0.9 },
  });
  const subtitleProgress = spring({
    frame: frame - introEnd - 2,
    fps,
    config: { damping: 22, stiffness: 120 },
  });
  const captionProgress = spring({
    frame: frame - introEnd - 10,
    fps,
    config: { damping: 24, stiffness: 110 },
  });
  const numberProgress = spring({
    frame: frame - introEnd + 2,
    fps,
    config: { damping: 16, stiffness: 140 },
  });
  const titleGlitchX = glitchActive
    ? (hash(glitchWindow + 3) - 0.5) * 30
    : 0;

  // —— Ken Burns 缓慢放大 ——
  const kbScale = interpolate(frame, [0, durationInFrames], [1.05, 1.12], {
    extrapolateRight: "clamp",
  });

  // —— 故障色条：瞬时水平撕裂条带 ——
  const bars: React.ReactNode[] = [];
  if (glitchActive) {
    const barCount = 3 + Math.floor(hash(glitchWindow + 9) * 3);
    for (let i = 0; i < barCount; i++) {
      const seed = glitchWindow * 7 + i;
      const top = hash(seed) * 100;
      const height = 1 + hash(seed + 1) * 5;
      const shiftX = (hash(seed + 2) - 0.5) * 80;
      const tint = hash(seed + 3);
      bars.push(
        <div
          key={`bar-${glitchWindow}-${i}`}
          style={{
            position: "absolute",
            left: `${shiftX}px`,
            top: `${top}%`,
            width: "100%",
            height: `${height}%`,
            background:
              tint > 0.66
                ? "rgba(255, 40, 80, 0.18)"
                : tint > 0.33
                ? "rgba(50, 255, 180, 0.14)"
                : "rgba(60, 120, 255, 0.18)",
            mixBlendMode: "screen",
            pointerEvents: "none",
          }}
        />
      );
    }
  }

  // —— 通道滤镜的唯一 ID，避免与其它组件冲突 ——
  const rId = "cs-red-70";
  const gId = "cs-green-70";
  const bId = "cs-blue-70";

  return (
    <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
      {/* SVG 颜色通道分离滤镜 */}
      <svg
        width="0"
        height="0"
        style={{ position: "absolute", pointerEvents: "none" }}
      >
        <defs>
          <filter id={rId} colorInterpolationFilters="sRGB">
            <feColorMatrix
              type="matrix"
              values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"
            />
          </filter>
          <filter id={gId} colorInterpolationFilters="sRGB">
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0"
            />
          </filter>
          <filter id={bId} colorInterpolationFilters="sRGB">
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0"
            />
          </filter>
        </defs>
      </svg>

      {/* 三通道图像叠加（screen 混合还原为完整色彩） */}
      <AbsoluteFill style={{ transform: `scale(${kbScale})` }}>
        <Img
          src={imageUrl}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: `url(#${rId})`,
            transform: `translate(${rX}px, 0)`,
            mixBlendMode: "screen",
          }}
        />
        <Img
          src={imageUrl}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: `url(#${gId})`,
            transform: `translate(0, ${gY}px)`,
            mixBlendMode: "screen",
          }}
        />
        <Img
          src={imageUrl}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: `url(#${bId})`,
            transform: `translate(${bX}px, ${-gY}px)`,
            mixBlendMode: "screen",
          }}
        />
      </AbsoluteFill>

      {/* 故障色条 */}
      <AbsoluteFill>{bars}</AbsoluteFill>

      {/* 扫描线（横向细纹，CRT 感） */}
      <AbsoluteFill
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 3px)",
          mixBlendMode: "overlay",
          pointerEvents: "none",
        }}
      />

      {/* 胶片颗粒 */}
      <AbsoluteFill
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
          mixBlendMode: "overlay",
          opacity: 0.32,
          pointerEvents: "none",
        }}
      />

      {/* 边缘暗角 */}
      <AbsoluteFill
        style={{
          boxShadow: "inset 0 0 220px 40px rgba(0,0,0,0.85)",
          pointerEvents: "none",
        }}
      />

      {/* 底部渐变，提升文字可读性 */}
      <AbsoluteFill
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.6) 80%, rgba(0,0,0,0.9) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* 顶部 caption */}
      <div
        style={{
          position: "absolute",
          top: 40,
          left: 56,
          right: 56,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "rgba(255,255,255,0.85)",
          letterSpacing: "0.32em",
          fontSize: 14,
          fontWeight: 500,
          fontFamily:
            "'SF Mono', 'JetBrains Mono', 'Courier New', monospace",
          opacity: captionProgress,
          transform: `translateY(${(1 - captionProgress) * -10}px)`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#ff3b30",
              boxShadow: "0 0 10px #ff3b30",
              opacity: frame % 30 < 15 ? 1 : 0.35,
            }}
          />
          SIGNAL · {caption}
        </div>
        <div>{`EFFECT // 0${number}`}</div>
      </div>

      {/* 右上角大号编号 */}
      <div
        style={{
          position: "absolute",
          top: 72,
          right: 56,
          color: "rgba(255,255,255,0.12)",
          fontSize: 200,
          fontWeight: 900,
          lineHeight: 1,
          letterSpacing: "-0.06em",
          fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
          transform: `translateX(${
            (1 - numberProgress) * 60
          }px) scale(${0.9 + numberProgress * 0.1})`,
          opacity: numberProgress * 0.9,
          mixBlendMode: "overlay",
          pointerEvents: "none",
        }}
      >
        {number}
      </div>

      {/* 左下主内容 */}
      <div
        style={{
          position: "absolute",
          left: 56,
          bottom: 72,
          right: 56,
          color: "#fff",
          fontFamily: "'Inter', 'PingFang SC', 'Helvetica Neue', sans-serif",
        }}
      >
        {/* 装饰彩色分离条 */}
        <div
          style={{
            position: "relative",
            width: 72,
            height: 4,
            marginBottom: 22,
            opacity: captionProgress,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "#ff2e63",
              transform: `translateX(${-baseSplit * 0.15}px)`,
              mixBlendMode: "screen",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "#00f5a0",
              mixBlendMode: "screen",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "#4d9bff",
              transform: `translateX(${baseSplit * 0.15}px)`,
              mixBlendMode: "screen",
            }}
          />
        </div>

        {/* 主标题 + RGB glitch 层 */}
        <div
          style={{
            position: "relative",
            marginBottom: 16,
            fontSize: 92,
            fontWeight: 800,
            lineHeight: 1.02,
            letterSpacing: "-0.02em",
            transform: `translateY(${(1 - titleProgress) * 40}px)`,
            opacity: titleProgress,
          }}
        >
          {/* 红通道幽灵 */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              color: "#ff2e63",
              transform: `translate(${-baseSplit * 0.12 + titleGlitchX}px, 0)`,
              mixBlendMode: "screen",
              textShadow: "0 0 1px rgba(255,46,99,0.5)",
            }}
          >
            {title}
          </div>
          {/* 蓝通道幽灵 */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              color: "#4d9bff",
              transform: `translate(${baseSplit * 0.12 - titleGlitchX}px, ${
                gY * 0.4
              }px)`,
              mixBlendMode: "screen",
            }}
          >
            {title}
          </div>
          {/* 主层 */}
          <div
            style={{
              position: "relative",
              color: "#fff",
              textShadow: "0 4px 24px rgba(0,0,0,0.6)",
            }}
          >
            {title}
          </div>
        </div>

        {/* 副标题 */}
        <div style={{ overflow: "hidden" }}>
          <div
            style={{
              fontSize: 24,
              fontWeight: 400,
              letterSpacing: "0.08em",
              color: "rgba(255,255,255,0.85)",
              transform: `translateY(${(1 - subtitleProgress) * 100}%)`,
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <span
              style={{
                width: 36,
                height: 1,
                background: "rgba(255,255,255,0.6)",
                display: "inline-block",
              }}
            />
            {subtitle}
          </div>
        </div>
      </div>

      {/* 右下角时间码 + 通道信息 */}
      <div
        style={{
          position: "absolute",
          right: 56,
          bottom: 56,
          color: "rgba(255,255,255,0.75)",
          fontFamily:
            "'SF Mono', 'JetBrains Mono', 'Courier New', monospace",
          fontSize: 13,
          letterSpacing: "0.2em",
          textAlign: "right",
          opacity: captionProgress,
          lineHeight: 1.6,
        }}
      >
        <div>
          {`CH · R${rX >= 0 ? "+" : ""}${rX.toFixed(1)}  G${gY >= 0 ? "+" : ""}${gY.toFixed(
            1
          )}  B${bX >= 0 ? "+" : ""}${bX.toFixed(1)}`}
        </div>
        <div>
          {`TC ${String(Math.floor(frame / fps)).padStart(2, "0")}:${String(
            frame % fps
          ).padStart(2, "0")} / ${String(
            Math.floor(durationInFrames / fps)
          ).padStart(2, "0")}:${String(durationInFrames % fps).padStart(
            2,
            "0"
          )}`}
        </div>
      </div>
    </AbsoluteFill>
  );
};
